// src/App.js

import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import './App.css';
import { Toaster, toast } from 'react-hot-toast';

// --- Import the API to fetch settings ---
import { api } from './api'; 

import Layout from './components/layout/Layout';
import LandingPage from './pages/LandingPage';
import DashboardScreen from './pages/DashboardScreen';
import PropertiesScreen from './pages/PropertiesScreen';
import SettingsScreen from './pages/SettingsScreen';
import ReportsScreen from './pages/ReportsScreen';
import HousekeepingScreen from './pages/HousekeepingScreen';
import BillingScreen from './pages/BillingScreen';
import CasesScreen from './pages/CasesScreen';
import CaseDetailScreen from './pages/CaseDetailScreen';
import DomainScreen from './pages/DomainsScreen';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EventLogViewer from './pages/EventLogViewer';
import HousekeeperPortal from './pages/HousekeeperPortal';
import SocialScreen from './pages/SocialScreen';


const getUserFromToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        return null;
    }
    try {
        const decodedToken = jwtDecode(token);
        return decodedToken;
    } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        return null;
    }
};

export default function App() {
  const [currentUser, setCurrentUser] = useState(getUserFromToken());
  const [isAuthenticated, setIsAuthenticated] = useState(!!currentUser);
  
  // Initialize activeView from localStorage, or default to dashboard
  const [activeView, setActiveView] = useState(() => localStorage.getItem('activeView') || 'dashboard');

  const [viewingCaseId, setViewingCaseId] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('activeView', activeView);
    }
  }, [activeView, isAuthenticated]);

  const handleLoginSuccess = async (accessToken, refreshToken) => {
    console.log("--- handleLoginSuccess function in App.js was called ---");

    if (accessToken && refreshToken) {
        console.log("✅ Both tokens look valid. Saving to localStorage...");
        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        const user = getUserFromToken();
        setCurrentUser(user);
        setIsAuthenticated(true);

        // --- Check User Preference for Landing Page ---
        try {
            const preferences = await api.getUserPreferences();
            const landingPref = preferences.find(p => p.preference_key === 'default_landing_page');
            
            if (landingPref && landingPref.preference_value) {
                console.log("Redirecting to user preference:", landingPref.preference_value);
                setActiveView(landingPref.preference_value);
            } else {
                setActiveView('dashboard');
            }
        } catch (error) {
            console.error("Could not load preferences on login, defaulting to dashboard.", error);
            setActiveView('dashboard');
        }

    } else {
        console.error("❌ FAILED: Login success was called but one or both tokens were missing.");
    }
  };

  // --- UPDATED: Smart Logout Function ---
  const handleLogout = () => {
      // 1. Check if we are in "Impersonation Mode"
      const adminToken = localStorage.getItem('adminAuthToken');
      const adminRefreshToken = localStorage.getItem('adminRefreshToken');

      if (adminToken) {
          // --- SCENARIO A: RESTORE ADMIN SESSION ---
          console.log("Restoring Admin Session...");
          
          // Restore the original Admin tokens
          localStorage.setItem('authToken', adminToken);
          if (adminRefreshToken) {
              localStorage.setItem('refreshToken', adminRefreshToken);
          }

          // Clear the "Stash"
          localStorage.removeItem('adminAuthToken');
          localStorage.removeItem('adminRefreshToken');

          // Optional: Show feedback
          toast.success("Returned to Admin Account");

          // Force a reload to ensure all states/permissions reset cleanly to Admin
          setTimeout(() => {
              window.location.href = '/'; 
          }, 500);
          
          return; // Stop here, do not execute normal logout
      }

      // --- SCENARIO B: ACTUAL LOGOUT ---
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('activeView'); 
      setCurrentUser(null);
      setIsAuthenticated(false);
      setActiveView('dashboard'); // Reset to default on logout
  };

  const renderMainApp = () => {
    switch (activeView) {
      case 'dashboard': return <DashboardScreen user={currentUser} />;
      case 'properties': return <PropertiesScreen user={currentUser} />;
      case 'settings': return <SettingsScreen user={currentUser} />;
      case 'reports': return <ReportsScreen user={currentUser} />;
      case 'Housekeeping': return <HousekeepingScreen user={currentUser} />;
      case 'billing': return <BillingScreen user={currentUser} />;
      case 'eventLogs': return <EventLogViewer user={currentUser} />;
      case 'support':
        return viewingCaseId ? 
            <CaseDetailScreen caseId={viewingCaseId} setViewingCaseId={setViewingCaseId} user={currentUser} /> : 
            <CasesScreen user={currentUser} setViewingCaseId={setViewingCaseId} />;
      case 'domains': return <DomainScreen user={currentUser} />;
      case 'social': return <SocialScreen user={currentUser} />;
      default: return <DashboardScreen user={currentUser} />;
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
        }}
      />
      
      {(() => {
        const path = window.location.pathname;

        if (path.startsWith('/reset-password/')) {
          const token = path.split('/')[2];
          return <ResetPasswordPage token={token} />;
        }

        if (path === '/portal') {
            return <HousekeeperPortal />;
        }
        
        if (!isAuthenticated) {
          return <LandingPage onLoginSuccess={handleLoginSuccess} />;
        }

        return (
          <Layout
              user={currentUser}
              activeView={activeView}
              setActiveView={setActiveView}
              onLogout={handleLogout}
          >
            {renderMainApp()}
          </Layout>
        );
      })()}
    </>
  );
}